/*
Shared slug / URL helpers for the static doc system.

These are used in BOTH places that must agree on URLs:
- the build-time generator (bin/) which emits one /slug/index.html per doc
- the runtime <tosi-doc-system> component which resolves location.pathname -> doc
  and builds nav hrefs.

Because the generator and the component both operate on the same docs.json, every
function here is a pure, order-independent function of the docs array, so the two
sides always produce identical slugs.
*/

export interface DocLike {
  filename: string
}

const README = /^readme\.md$/i

/** filename without its final extension; README.md maps to '' (the site root) */
function baseSlug(filename: string): string {
  if (README.test(filename)) return ''
  return filename.replace(/\.[^.]+$/, '')
}

/**
 * Build a deterministic { filename -> slug } map for a set of docs.
 *
 * Collisions (e.g. `layout.ts` and `layout.css` both -> `layout`) are resolved by
 * falling back to the dotted filename with dots turned into dashes for EVERY member
 * of the colliding group. Computed from counts first, so the result does not depend
 * on array order.
 */
export function buildSlugMap(docs: DocLike[]): Record<string, string> {
  const counts: Record<string, number> = {}
  for (const doc of docs) {
    const base = baseSlug(doc.filename)
    counts[base] = (counts[base] || 0) + 1
  }
  const map: Record<string, string> = {}
  for (const doc of docs) {
    const base = baseSlug(doc.filename)
    // README ('' base) is unique by construction; never disambiguate it.
    map[doc.filename] =
      base !== '' && counts[base] > 1
        ? doc.filename.replace(/\./g, '-')
        : base
  }
  return map
}

/** site-root-relative path for a slug: '' -> '/', 'button' -> '/button/' */
export function pathForSlug(slug: string): string {
  return slug === '' ? '/' : `/${slug}/`
}

/**
 * Rewrite legacy `?<filename>` (and `/?<filename>`) doc links in an HTML string
 * to resolved hrefs. Used by the static generator so the pre-rendered pages have
 * clean links for no-JS readers, crawlers, and the brief window before the
 * doc-browser hydrates. `hrefFor` returns the target href for a known filename,
 * or null to leave the link untouched (unknown filename / a real query string).
 */
export function rewriteDocLinks(
  html: string,
  hrefFor: (filename: string) => string | null
): string {
  return html.replace(/href="\/?\?([^"&=]+)"/g, (match, query: string) => {
    const href = hrefFor(decodeURIComponent(query))
    return href === null ? match : `href="${href}"`
  })
}

/**
 * Map a legacy `?<filename>` query (the old doc-browser's query-param routing,
 * e.g. `?button.ts`, `?README.md`) to the new slug path (`/button/`, `/`).
 *
 * Returns the new path, or null when `search` isn't a bare-filename query or
 * doesn't match a known doc. Uses the slug map so README -> '/' and collision
 * disambiguation (foo.ts + foo.css -> /foo-ts/, /foo-css/) are handled.
 */
export function legacyQueryPath(
  search: string,
  slugMap: Record<string, string>
): string | null {
  const query = search.replace(/^\?/, '')
  // Legacy links are a single bare filename — anything with key=value pairs is
  // a real query string, not the old routing.
  if (!query || query.includes('=') || query.includes('&')) return null
  const filename = decodeURIComponent(query)
  const slug = slugMap[filename]
  if (slug === undefined) return null
  return pathForSlug(slug)
}

/** strip a pathname down to its slug: '/button/' -> 'button', '/' -> '' */
export function slugForPath(pathname: string): string {
  return pathname.replace(/^\/+/, '').replace(/\/+$/, '').replace(/\/index\.html$/i, '')
}

/**
 * Resolve a browser location.pathname to a doc filename, using a slug map.
 * Returns '' if nothing matches (caller falls back to the first/README doc).
 */
export function filenameForPath(
  pathname: string,
  slugMap: Record<string, string>
): string {
  const slug = slugForPath(pathname)
  for (const [filename, docSlug] of Object.entries(slugMap)) {
    if (docSlug === slug) return filename
  }
  return ''
}

/** Turn a human name/title into a slug: 'Form Components' -> 'form-components'. */
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Resolve a doc's `parent` value (a NAME or a slug) to the parent doc's
 * filename. Tries, in order: exact filename, exact slug (slug->slug no-op),
 * slugify(value) against doc slugs, then slugify(value) against slugify(title).
 * Returns '' if nothing matches (the build then auto-creates a section doc).
 */
export function resolveParent(
  parentValue: string,
  docs: Array<{ filename: string; title?: string }>,
  slugMap: Record<string, string>
): string {
  if (!parentValue) return ''
  for (const d of docs) if (d.filename === parentValue) return d.filename
  for (const d of docs) if (slugMap[d.filename] === parentValue) return d.filename
  const target = slugify(parentValue)
  for (const d of docs) if (slugMap[d.filename] === target) return d.filename
  for (const d of docs) if (d.title && slugify(d.title) === target) return d.filename
  return ''
}
